import { Link } from 'src/design-system'
import { observer } from 'mobx-react-lite'
import React from 'react'
import { Discipline } from 'store/modules/disciplines'
import { InfoPane } from './info-pane'
import { LoadingContent } from 'components/loading-content'
import { ModifiablePositionAttributes } from 'store/modules/positions'
import { MovePositionDialog } from 'components/move-position-dialog'
import { Nav } from './nav'
import { PositionModalHeader } from './header'
import { RequiredSkillsSection } from './required-skills-section'
import { errorToast } from '../../utils/error-toast'
import { extractServerErrorMessage } from 'src/utils/extract-server-error'
import { successToast } from '../../utils/success-toast'
import { trackEvent } from '../../services/event-tracker'
import { useStore } from 'store/context'
import * as MegaModal from '../mega-modal'

export type PositionModalContentProps = {
  disciplineId?: string
  positionId: string
  onChangePosition?: (positionId: string, disciplineId: string) => void
  showCloseButton?: boolean
  source?: string
}

export const PositionModalContent = observer<PositionModalContentProps>(
  ({ disciplineId, positionId, onChangePosition, showCloseButton, source }) => {
    const {
      changes,
      comments,
      currentUser,
      disciplines,
      positions,
      positionSkills,
    } = useStore()

    const [errors, setErrors] = React.useState<string[]>([])

    const [formState, setFormState] = React.useState<
      Partial<ModifiablePositionAttributes>
    >({})

    const [loading, setLoading] = React.useState(true)

    // Incrementing this causes Effect 1 to re-run even if positionId didn't
    // change — used to re-fetch after a positions:sorted event fires.
    const [refreshKey, setRefreshKey] = React.useState(0)

    const position = positions.byId(positionId, { useDefaultFilter: false })

    const discipline =
      disciplines.byId(disciplineId) ?? (position?.disciplines[0] as Discipline)

    // When a drag-and-drop sort commits on the server, the modal may still be
    // showing data fetched before the commit (race: user clicked too fast).
    // Incrementing refreshKey triggers Effect 1 to re-fetch with correct data.
    React.useEffect(() => {
      if (!positionId) return
      const handler = () => setRefreshKey((k) => k + 1)
      document.addEventListener('positions:sorted', handler)
      return () => document.removeEventListener('positions:sorted', handler)
    }, [positionId])

    React.useEffect(() => {
      if (!positionId) return

      // Show spinner immediately so stale formState is never visible while the
      // fresh fetch is in-flight. Without this, loading stays false from the
      // previous open and stale Label/Seniority data is shown on re-open.
      setLoading(true)

      let cancelled = false

      const fetchData = async () => {
        const positionFetch = positions.fetchOne(
          positionId,
          {
            include: [
              'framework',
              'framework.disciplines',
              'framework.frameworks_skills.category',
              'framework.frameworks_skills.skill_variant.skill',
              'framework.positions',
              'requirements',
              'requirements.skill_level',
              'requirements.skill_level.outcomes',
              'salaries',
            ],
          },
          // Always bypass the 10-second GET cache so the modal never displays
          // data from before a drag-and-drop sort (the cache is not invalidated
          // by the sort PATCH because it targets a different URL).
          { bypassCache: true },
        )

        const positionSkillsFetch = positionSkills.fetchForPosition(positionId)

        await Promise.all([positionFetch, positionSkillsFetch])

        if (cancelled) return

        // MST updates models in-place, so the `position` reference never changes
        // after fetchOne resolves — the `[position, source]` useEffect below won't
        // re-run. Read formState from the now-updated observable before revealing
        // the modal so fields like seniorityLevel always reflect server state.
        const freshPosition = positions.byId(positionId, {
          useDefaultFilter: false,
        })
        if (freshPosition) {
          setFormState({
            customLabel: freshPosition.customLabel,
            description: freshPosition.description,
            disciplines: freshPosition.disciplineIds,
            hiringLink: freshPosition.hiringLink,
            name: freshPosition.name,
            seniorityLevel: freshPosition.seniorityLevel,
          })
        }

        setLoading(false)
      }

      fetchData()

      comments.fetchAll({
        filter: { commentable_id: positionId, commentable_type: 'Position' },
      })

      return () => {
        cancelled = true
      }
    }, [comments, positionId, positions, positionSkills, refreshKey])

    React.useEffect(() => {
      if (!position) return

      setFormState({
        customLabel: position.customLabel,
        description: position.description,
        disciplines: position.disciplineIds,
        hiringLink: position.hiringLink,
        name: position.name,
        seniorityLevel: position.seniorityLevel,
      })

      trackEvent('$track_position_viewed', {
        format: 'modal',
        positionId: position.id,
        positionName: position.name,
        source,
      })
    }, [position, source])

    const editable =
      currentUser &&
      position?.team &&
      ((position.team.org && currentUser.isAdminOfOrg(position.team.org.id)) ||
        position.team.hasEditor(currentUser.id))

    const clearFieldErrors = (field: string) => {
      setErrors(errors.filter((error) => error !== field))
    }

    const loadHealthAudit = async () => {
      try {
        const response = await fetch(
          `/api/v1/health_audits/position_audit?position_id=${positionId}`,
        )
        if (response.ok) {
          const data = await response.json()
          document.dispatchEvent(
            new CustomEvent('positionHealthAuditUpdated', {
              detail: { messages: data.messages },
            }),
          )
        }
      } catch (error) {
        console.error('Position health audit check failed:', error)
      }
    }

    const onBlur = async (
      field: keyof typeof formState,
      resetOnFailure?: boolean,
    ) => {
      if (!position) return
      if (formState[field] === position[field]) return

      const result = await positions.update(position.id, formState)
      if (result.success) {
        clearFieldErrors(field)
        if (field === 'description') {
          setErrors(errors.filter((error) => error !== 'description'))
          loadHealthAudit()
        }
        successToast('Position updated')
        document.dispatchEvent(new Event('hydrate'))
        changes.fetchForResource(positionId, 'Position', 1)
      } else {
        const serverMessage = extractServerErrorMessage(result)
        if (serverMessage) errorToast(serverMessage)

        if (resetOnFailure) {
          clearFieldErrors(field)
          setField(field, position[field])
        } else {
          if (!errors.includes(field)) setErrors([...errors, field])
        }
      }
    }

    const setField = <Field extends keyof ModifiablePositionAttributes>(
      field: Field,
      value: ModifiablePositionAttributes[Field] | null,
    ) => {
      setFormState((formState) => ({ ...formState, [field]: value }))
    }

    return (
      <LoadingContent className="pt-10" loading={loading}>
        {position ? (
          <>
            <MegaModal.Nav>
              <Nav
                discipline={discipline}
                editable={editable}
                position={position}
                onChangePosition={onChangePosition}
                showCloseButton={showCloseButton}
              />
            </MegaModal.Nav>
            <MegaModal.Body>
              <MegaModal.Header>
                <PositionModalHeader
                  editable={editable}
                  errors={errors}
                  formState={formState}
                  onBlur={onBlur}
                  onChange={setField}
                  position={position}
                />
              </MegaModal.Header>
              <MegaModal.Content>
                <RequiredSkillsSection
                  editable={editable}
                  position={position}
                />
              </MegaModal.Content>
            </MegaModal.Body>
            <MegaModal.RightPane>
              <InfoPane
                editable={editable}
                errors={errors}
                formState={formState}
                onBlur={onBlur}
                onChange={setField}
                position={position}
              />
            </MegaModal.RightPane>
            <MovePositionDialog />
          </>
        ) : (
          <NotFound />
        )}
      </LoadingContent>
    )
  },
)

const NotFound: React.VFC = () => {
  return (
    <div className="min-h-screen w-full h-full flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-3xl font-bold mb-4">Position not found</h1>
        <p className="text-gray-600">We could not find this Position.</p>
        <Link
          href="/"
          variant="brand"
          data-turbo-frame="content"
          data-turbo-action="advance"
        >
          Back Home
        </Link>
      </div>
    </div>
  )
}
